// ============================================================================
// finApi — cliente do /api/fin (Financeiro Fase 1b + 1d, 06/10/2026)
// ============================================================================
// Endpoint ÚNICO do Financeiro no servidor (api/fin.js): só o OPERADOR
// financeiro (master ou lista FIN_OPERADORES). GET com ?op=…, POST com corpo
// JSON {op, …}. Todo dinheiro em CENTAVOS inteiros.
//
// REGRA LINEAR: falhou → lança FinApiErro com a mensagem do servidor (em
// português), o HTTP (status), o código (code) e o corpo inteiro (dados — ex.:
// a lista de conflitos do folha-pagar, o checklist do so_direcao_com_ressalvas).
// Nunca devolve "vazio" no lugar de um erro.
// ============================================================================

import { sessionToken } from './secureRest.js';

const BASE = '/api/fin';

export class FinApiErro extends Error {
  constructor(message, { status = 0, code = 'erro', dados = null } = {}) {
    super(message);
    this.name = 'FinApiErro';
    this.status = status;
    this.code = code;
    this.dados = dados || {};
  }
}

// Parâmetros de GET sem null/undefined/'' (o servidor recusa "competencia=").
function limparParams(params) {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

async function chamar(method, params, { fetchImpl } = {}) {
  const f = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const token = sessionToken();
  if (!token) throw new FinApiErro('Sessão expirada. Entre de novo.', { status: 401, code: 'sem_sessao' });
  const url = method === 'GET' ? `${BASE}?${new URLSearchParams(limparParams(params))}` : BASE;
  let r;
  try {
    r = await f(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'x-session-token': token },
      body: method === 'GET' ? undefined : JSON.stringify(params),
      cache: 'no-store',
    });
  } catch {
    throw new FinApiErro('Sem resposta do servidor (conexão). Tente de novo em instantes.', { status: 0, code: 'rede' });
  }
  const text = await r.text().catch(() => '');
  const trimmed = String(text || '').trim();
  let j = null;
  if (trimmed && !trimmed.startsWith('<')) {
    try { j = JSON.parse(trimmed); } catch { j = null; }
  }
  if (!r.ok) {
    const padrao = r.status === 504
      ? 'O servidor demorou demais para responder (tempo esgotado). Tente de novo.'
      : `Erro ${r.status} ao falar com o servidor.`;
    throw new FinApiErro((j && typeof j.error === 'string' && j.error) || padrao, {
      status: r.status, code: (j && j.code) || `http_${r.status}`, dados: j && typeof j === 'object' ? j : null,
    });
  }
  if (!j || typeof j !== 'object') {
    // HTML/fallback servido no lugar da API (deploy, SW, plataforma)
    throw new FinApiErro('Resposta inesperada do servidor (tente de novo).', { status: r.status, code: 'resposta' });
  }
  return j;
}

export const finGet = (op, params = {}, opts) => chamar('GET', { op, ...params }, opts);
export const finPost = (corpo, opts) => chamar('POST', corpo, opts);

// ─── Atalhos (o contrato está em api/fin.js) ────────────────────────────────
// 09/10/2026 — "Oscar Freire + Jardins juntas": as leituras da DRE aceitam
// visao 'grupo' (padrão do servidor: a coluna "Oscar Freire + Jardins (TREE)")
// ou 'unidades' (as duas separadas). Calculado na LEITURA: a versão gravada e o
// hash não mudam. visao vazia = o padrão do servidor (o mesmo pedido de antes).
const VISOES_DRE = new Set(['grupo', 'unidades']);
const visaoOk = (v) => (VISOES_DRE.has(v) ? v : undefined);
/** PRÉVIA da DRE (não grava): { previa, dre, checklist, checagens, parametros, versoes, etiquetas, visao, … } */
export const carregarDrePrevia = (competencia, { visao } = {}) => finGet('dre', { competencia, visao: visaoOk(visao) });
/** Checagens (a)–(j) da competência. */
export const carregarChecagens = (competencia) => finGet('checagens', { competencia });
/** Versões gravadas (sem as linhas), competência desc, versão desc. */
export const listarVersoesDre = (competencia = null) => finGet('dre-versoes', { competencia });
/** Uma versão inteira: { versao: { …, linhas: DRE, checklist, parametros }, etiquetas, dre_visao? } */
export const carregarVersaoDre = (id, { visao } = {}) => finGet('dre-versao', { id, visao: visaoOk(visao) });
/** Margem por unidade (versão gerada, senão prévia). */
export const carregarTendenciaDre = ({ ate, meses, visao } = {}) => finGet('dre-tendencia', { ate, meses, visao: visaoOk(visao) });
/** Grava a versão (checklist T9). justificativas só valem para a, b, d, e, f, g. */
export const gerarDre = ({ competencia, motivo, justificativas = {} }) =>
  finPost({ op: 'dre-gerar', competencia, motivo, justificativas });
/** "Marcar paga" / "reabrir" da Folha (corpo montado por utils/folhaContasPagar.js). */
export const folhaPagar = (corpo) => finPost({ ...corpo, op: 'folha-pagar' });
/** Plano de contas ATIVO + de-para: { categorias: [{codigo, nome, linha_dre, bloco, grupo_ui, ordem, a_validar, observacao}], alias: [{valor_atual, codigo}] } */
export const carregarCategorias = () => finGet('categorias');
/** Grupos financeiros (09/10/2026): { grupos: [{ id, rotulo, unidades, principal, custo_de, ativo, … }], hoje } */
export const carregarGrupos = (opts) => finGet('grupos', {}, opts);
/** Contas do Financeiro (09/10/2026): { contas: [Caixa + Stone], padrao_por_unidade, antigas } */
export const carregarContasBancarias = (opts) => finGet('contas-bancarias', {}, opts);

// 16/10/2026 — Fluxo de Caixa MENSAL pela conta Stone (motor api/_lib/fin-fluxo.js: o formato da
// resposta é o `return` de calcularFluxoMensal). Só GET, nada grava. Resposta fora do formato = erro
// (REGRA LINEAR: nunca vira um mês vazio). Aceita o modelo direto ou dentro de { fluxo }.
const ehFluxoMensal = (r) => !!r && typeof r === 'object' && /^\d{4}-(0[1-9]|1[0-2])$/.test(String(r.competencia || ''))
  && Array.isArray(r.colunas) && Array.isArray(r.linhas) && !!r.resumo && typeof r.resumo === 'object';
const ehFluxoAno = (r) => !!r && typeof r === 'object' && /^\d{4}$/.test(String(r.ano || '')) && Array.isArray(r.meses);
/** Fluxo do mês: { competencia, periodo, colunas, contas, rede, linhas, resumo, conferencia, gaveta, vendido_recebido, aviso_fonte, etiquetas, … } */
export async function carregarFluxoMensal(competencia, opts) {
  const r = await finGet('fluxo', { competencia }, opts);
  const m = ehFluxoMensal(r) ? r : (ehFluxoMensal(r?.fluxo) ? r.fluxo : null);
  if (!m) throw new FinApiErro('O Fluxo de Caixa veio em formato inesperado (tente de novo).', { status: 200, code: 'formato', dados: r });
  return m;
}
/** Fluxo do ano (só as linhas macro e a fonte de cada mês): { ano, meses: [{ competencia, periodo, fonte, rede, linhas, status, aviso_fonte }] }.
 *  O servidor pede também a competência (o mês da tela); sem ela, janeiro do ano. */
export async function carregarFluxoAno(ano, { competencia } = {}, opts) {
  const comp = /^\d{4}-(0[1-9]|1[0-2])$/.test(String(competencia || '')) ? competencia : `${ano}-01`;
  const r = await finGet('fluxo', { competencia: comp, ano }, opts);
  const m = ehFluxoAno(r) ? r : (ehFluxoAno(r?.fluxo) ? r.fluxo : (ehFluxoAno(r?.ano_fluxo) ? r.ano_fluxo : null));
  if (!m) throw new FinApiErro('O Fluxo do ano veio em formato inesperado (tente de novo).', { status: 200, code: 'formato', dados: r });
  return m;
}

// 17/10/2026 — a reserva de cada conta Stone do "Disponível para retirada": só o master grava (o servidor
// recusa os outros com 403). contas = { <conta_id>: valor_cents }.
export const salvarReservaRetirada = (contas) => finPost({ op: 'reserva-retirada', contas });

export default {
  finGet, finPost, carregarDrePrevia, carregarChecagens, listarVersoesDre, carregarVersaoDre, carregarTendenciaDre, gerarDre, folhaPagar,
  carregarCategorias, carregarGrupos, carregarContasBancarias, carregarFluxoMensal, carregarFluxoAno, salvarReservaRetirada,
};
